import { prettyDate, type TatPlan } from './TatPlanNotice';

/**
 * Budget vs actual for one indent — where the 9 days went, stage by stage.
 *
 * The floor (TatPlanNotice) says what we promised. This says what happened:
 * how long each hand-off actually took against its budget, which stage the
 * indent is sitting in now, and how many days are left before the promise
 * breaks. Everything is computed server-side from statusHistory
 * (services/procurement/procurement-tat-progress.ts).
 */

export type TatStageProgress = {
  stage: string;
  what: string;
  designation: string;
  person: string;
  note?: string;
  budgetDays: number;
  startedAt: string | null;
  endedAt: string | null;
  actualDays: number | null;
  varianceDays: number | null;
  state: 'done' | 'current' | 'pending';
  /** The status jumped over this stage, so there is no record either way.
   *  NOT a statement that the work was done — see SKIPPED_HINT. */
  skipped?: boolean;
  statusesSeen: string[];
};

export type TatProgress = {
  indentId: string;
  indentType: string;
  plan: TatPlan | null;
  startedAt: string | null;
  dueDate: string | null;
  promisedDate: string;
  elapsedDays: number | null;
  remainingDays: number | null;
  completedAt: string | null;
  actualTotalDays: number | null;
  state: 'no_plan' | 'on_track' | 'at_risk' | 'overdue' | 'completed_on_time' | 'completed_late';
  currentStage: string | null;
  currentStatus: string;
  stages: TatStageProgress[];
  dataIssues: string[];
};

const STATE_LABEL: Record<TatProgress['state'], string> = {
  no_plan: 'No TAT schedule',
  on_track: 'On track',
  at_risk: 'At risk',
  overdue: 'Overdue',
  completed_on_time: 'Delivered on time',
  completed_late: 'Delivered late',
};

const STATE_TONE: Record<TatProgress['state'], string> = {
  no_plan: 'bg-slate-100 text-slate-600 border-slate-200',
  on_track: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  at_risk: 'bg-amber-50 text-amber-900 border-amber-200',
  overdue: 'bg-rose-50 text-rose-800 border-rose-200',
  completed_on_time: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  completed_late: 'bg-rose-50 text-rose-800 border-rose-200',
};

/**
 * What a "skipped" stage actually means.
 *
 * The status jumped straight past it — usually because the portal sheet was
 * edited to a later stage and the sync honoured it. That tells us nothing
 * about whether the work happened, so calling it "Passed" in green claimed a
 * completion nobody evidenced: on QAS17630 it showed PO release, payment and
 * CRO as passed on an order that had done none of them.
 */
const SKIPPED_HINT =
  'The status moved straight past this stage, so nothing was recorded for it. '
  + 'It is not evidence the work was done — check the stage before relying on it.';

/** Over budget is the only thing worth colouring — under budget is just good. */
function varianceTone(v: number | null): string {
  if (v === null) return 'text-slate-300';
  if (v > 0) return 'text-rose-700 font-semibold';
  if (v < 0) return 'text-emerald-700';
  return 'text-slate-500';
}

function Stat({ label, value, sub, tone }: { label: string; value: React.ReactNode; sub?: string; tone?: string }) {
  return (
    <div className={'rounded-lg border px-3 py-2 ' + (tone || 'border-slate-200 bg-white')}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-lg font-bold leading-tight tabular-nums">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500">{sub}</div>}
    </div>
  );
}

/** One thing you can do at a stage: where it goes, and how to get there. */
export type StageActionSpec = { label: string; run: () => void };

/**
 * Per-stage actions — deeplinks into the screens that actually complete a stage.
 *
 * Deliberately NOT "mark done" ticks. A stage finishes by doing the work, which
 * moves the indent's status, which is what this table reads; letting anyone tick
 * a stage would decouple the tracker from reality — the exact problem it exists
 * to expose. So these just carry you to the right place.
 *
 * A stage usually has SEVERAL real actions (sourcing alone has compare/award,
 * live quotes, manual entry, negotiations and LOI), so every one is listed
 * rather than guessing which the user wants.
 */
function StageActions({
  stage, chainDone, actions, startNo,
}: { stage: TatStageProgress; chainDone: boolean; actions: StageActionSpec[]; startNo: number }) {
  if (chainDone) return <span className="text-slate-300">—</span>;

  const done = stage.state === 'done';
  if (done && !actions.length) {
    return stage.skipped
      ? <span className="text-[11px] font-semibold text-slate-500" title={SKIPPED_HINT}>Not recorded</span>
      : <span className="text-[11px] font-semibold text-emerald-600">Done</span>;
  }
  if (!actions.length) return <span className="text-slate-300">—</span>;

  return (
    <div className="flex flex-wrap items-center justify-end gap-1">
      {done && (
        <span
          className={'mr-1 text-[10.5px] font-semibold ' + (stage.skipped ? 'text-slate-500' : 'text-emerald-600')}
          title={stage.skipped ? SKIPPED_HINT : undefined}
        >
          {stage.skipped ? 'Not recorded' : 'Done'}
        </span>
      )}
      {actions.map((a, i) => (
        <button
          key={a.label}
          type="button"
          onClick={a.run}
          title={`Step ${startNo + i} — go to: ${a.label}`}
          className={
            'press inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10.5px] font-semibold ' +
            // A finished stage keeps its actions (re-open quotes, re-send a doc)
            // but greys them so the live one still reads as the next thing to do.
            (done
              ? 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
              : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100')
          }
        >
          {/*
            The step number, counted straight down the whole pipeline.

            The buttons are the order the work actually happens in, but nothing
            said so — a desk looking at six of them on one row had to already
            know the sequence. A small ordinal is guidance only: nothing is
            enforced, and a finished stage keeps its buttons.
          */}
          <span
            aria-hidden
            className={
              'inline-flex h-[14px] min-w-[14px] items-center justify-center rounded-full px-[3px] text-[9px] font-bold leading-none '
              + (done ? 'bg-slate-100 text-slate-400' : 'bg-blue-600 text-white')
            }
          >
            {startNo + i}
          </span>
          {a.label} <span aria-hidden>→</span>
        </button>
      ))}
    </div>
  );
}

/**
 * A sign-off read out of the external survey sheet, keyed by plan stage.
 * Stages 3.1 / 3.2 are approvals that happen there, not status transitions
 * here, so their evidence is a name and a date rather than a measured span.
 */
export type StageSignOff = { byName?: string; at?: string; note?: string };

export default function TatProgressPanel(
  { p, stageActionsFor, signOffs }: {
    p: TatProgress;
    stageActionsFor?: (stage: string) => StageActionSpec[];
    signOffs?: Record<string, StageSignOff>;
  },
) {
  // Import has no schedule yet, so there is nothing to measure against.
  if (!p.plan) return null;

  const done = p.completedAt !== null;
  const spent = p.elapsedDays ?? 0;
  const budget = p.plan.minDays;
  // Bar can exceed 100% — that overflow IS the story on a 60-day-old indent.
  const pct = Math.min(100, Math.round((spent / Math.max(budget, 1)) * 100));
  const over = spent > budget;

  /**
   * Step numbers, counted straight down the pipeline.
   *
   * Resolved once here rather than inside the row so the running total is
   * continuous: stage 1's single action is step 1, stage 2's five are 2-6, and
   * so on. Numbering per stage instead would restart at 1 on every row and say
   * nothing about the order of the whole job.
   */
  const stageActions = p.stages.map((st) => (stageActionsFor ? stageActionsFor(st.stage) : []));
  const stepStartNo: number[] = [];
  let step = 1;
  for (const acts of stageActions) { stepStartNo.push(step); step += acts.length; }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat
          label="Days used"
          value={<>{spent}<span className="text-sm font-normal text-slate-400"> / {budget}</span></>}
          sub={done ? `Completed ${prettyDate(p.completedAt!)}` : 'Since the indent was raised'}
          tone={over ? 'border-rose-200 bg-rose-50' : undefined}
        />
        <Stat
          label={done ? 'Result' : 'Days remaining'}
          value={
            done
              ? <span className="text-base">{p.state === 'completed_late' ? 'Late' : 'On time'}</span>
              : <span className={p.remainingDays !== null && p.remainingDays < 0 ? 'text-rose-700' : ''}>
                  {p.remainingDays === null ? '—' : p.remainingDays < 0 ? `${Math.abs(p.remainingDays)} over` : p.remainingDays}
                </span>
          }
          sub={p.dueDate ? `TAT due ${prettyDate(p.dueDate)}` : undefined}
          tone={!done && p.remainingDays !== null && p.remainingDays < 0 ? 'border-rose-200 bg-rose-50' : undefined}
        />
        <Stat
          label="Currently at"
          value={<span className="text-base">{p.currentStage || (done ? 'Complete' : '—')}</span>}
          sub={p.currentStatus || undefined}
        />
        <Stat
          label="Promised to client"
          value={<span className="text-base">{p.promisedDate ? prettyDate(p.promisedDate) : 'Not set'}</span>}
          sub={
            p.promisedDate && p.dueDate
              ? (p.promisedDate < p.dueDate ? 'Earlier than the TAT allows' : 'Consistent with the TAT')
              : undefined
          }
          tone={p.promisedDate && p.dueDate && p.promisedDate < p.dueDate ? 'border-amber-200 bg-amber-50' : undefined}
        />
      </div>

      <div className="flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200">
          <div
            className={'h-full rounded-full ' + (over ? 'bg-rose-500' : pct > 70 ? 'bg-amber-500' : 'bg-emerald-500')}
            style={{ width: `${Math.max(pct, 2)}%` }}
          />
        </div>
        <span className={'shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ' + STATE_TONE[p.state]}>
          {STATE_LABEL[p.state]}
        </span>
      </div>

      {p.dataIssues.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900">
          <span className="font-semibold">Audit trail problem — </span>
          {p.dataIssues.join('; ')}. Stage timings below may be understated.
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full min-w-[36rem] text-left text-[12px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2 py-1.5 font-semibold">Stage</th>
              <th className="px-2 py-1.5 font-semibold">What happens</th>
              <th className="px-2 py-1.5 font-semibold">Owner</th>
              <th className="px-2 py-1.5 text-right font-semibold">Budget</th>
              <th className="px-2 py-1.5 text-right font-semibold">Actual</th>
              <th className="px-2 py-1.5 text-right font-semibold">Variance</th>
              <th className="px-2 py-1.5 text-right font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {p.stages.map((s, si) => (
              <tr
                key={s.stage}
                className={
                  'border-t border-slate-100 ' +
                  (s.state === 'current' ? 'bg-blue-50/60' : s.state === 'pending' ? 'text-slate-400' : '')
                }
              >
                <td className="whitespace-nowrap px-2 py-1.5 font-mono text-[10px]">
                  {s.stage}
                  {s.state === 'current' && (
                    <span className="ml-1 rounded bg-blue-600 px-1 py-0.5 text-[9px] font-bold text-white">NOW</span>
                  )}
                </td>
                <td className="px-2 py-1.5">
                  {s.what}
                  {s.skipped && (
                    <span
                      className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold text-amber-800"
                      title={SKIPPED_HINT}
                    >
                      NO RECORD
                    </span>
                  )}
                  {s.startedAt && (
                    <span className="ml-1 text-[10px] text-slate-400">
                      {prettyDate(s.startedAt)}{s.endedAt ? ` → ${prettyDate(s.endedAt)}` : ' → now'}
                    </span>
                  )}
                  {/* An approval stage has no span to show — its evidence is a
                      signature. Printed under the description so 3.1/3.2 say who
                      actually signed instead of an unexplained "Passed". */}
                  {signOffs?.[s.stage] && (
                    <div className="mt-0.5 text-[10px] text-emerald-700">
                      ✓ Approved by <b>{signOffs[s.stage].byName || 'unnamed approver'}</b>
                      {signOffs[s.stage].at ? ` · ${prettyDate(signOffs[s.stage].at!)}` : ''}
                      {signOffs[s.stage].note && (
                        <span className="text-emerald-600/80"> — {signOffs[s.stage].note}</span>
                      )}
                    </div>
                  )}
                </td>
                {/* Role, not the individual. People move teams and leave; a stage
                    is owned by a function. The named person stays on the tooltip
                    so the knowledge isn't lost, but the table won't quietly
                    credit work to someone who left months ago. */}
                <td className="whitespace-nowrap px-2 py-1.5" title={s.person ? `Currently: ${s.person}` : undefined}>
                  {s.designation || s.person || '—'}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">{s.budgetDays > 0 ? `${s.budgetDays}d` : '—'}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {s.actualDays === null ? <span className="text-slate-300">—</span> : `${s.actualDays}d`}
                </td>
                <td className={'px-2 py-1.5 text-right tabular-nums ' + varianceTone(s.varianceDays)}>
                  {s.varianceDays === null ? '—' : s.varianceDays > 0 ? `+${s.varianceDays}d` : `${s.varianceDays}d`}
                </td>
                <td className="whitespace-nowrap px-2 py-1.5 text-right">
                  <StageActions
                    stage={s}
                    chainDone={done}
                    actions={stageActions[si]}
                    startNo={stepStartNo[si]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
              <td className="px-2 py-1.5" colSpan={3}>Total</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{budget}d</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{spent}d</td>
              <td className={'px-2 py-1.5 text-right tabular-nums ' + varianceTone(spent - budget)}>
                {spent - budget > 0 ? `+${spent - budget}d` : `${spent - budget}d`}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
